import type { CSSProperties, ReactNode } from 'react';
import { libelleVenuPar, PREMIER_CONTACT } from '../../api/contrat.js';
import { Lien } from '../../app/navigation.js';
import { Pastille } from '../../app/pastille.js';
import { CIBLE_MIN, COULEURS, POLICE_EQUIPE, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { blocPlace, ceQuiManque, MANDAT_LIBELLE, repartirParcours, type PlaceBloc } from './avancement.js';
import { presenterBlocage } from './blocages.js';
import { formaterDuree } from './chronometre.js';
import { avecDossier, CHEMINS_VOISINS } from './chemins.js';
import { NavSections } from './sections.js';
import { joursDepuis, presenterDelai, type TonDelai } from './delais.js';
import { compteurForfait, type Consommation, type ForfaitInclus, type TypeAction } from './forfait.js';
import { LIBELLE_ETAT, resumeParcours, type EtatEtape } from './parcours.js';
import { peutActiver, type EtapeRecherche } from './recherche.js';
import { REFERENTIEL_L53 } from './referentiel.js';
import type { DossierVue, EntreeJournal } from './types.js';
import type { ReglageAppris } from './actions.js';
import reglagesVisibles from './donnees/reglages-visibles.json' with { type: 'json' };
import { QUESTIONS_BETA } from '../../../../../packages/contracts/src/beta/index.js';

export interface LibelleMetier {
  code: string;
  libelle: string;
}

export type EtatJournal =
  { etat: 'chargement' } | { etat: 'erreur'; message: string } | { etat: 'pret'; entrees: readonly EntreeJournal[] };

export interface VueEnsembleProps {
  dossier: DossierVue;
  /** Date du jour (AAAA-MM-JJ), fournie par l’appelant pour des délais reproductibles. */
  aujourdhui: string;
  mandantNom?: string | undefined;
  /** Type de dossier (#27) et statut métier (#30) : des libellés de référentiel, jamais écrits dans le code. */
  metier?: { type?: LibelleMetier; statut?: LibelleMetier } | undefined;
  recherche?: readonly EtapeRecherche[] | undefined;
  geometre?: boolean | undefined;
  forfait?: { inclus: ForfaitInclus; consomme: Consommation } | undefined;
  chrono?: { totalMs: number; enCours: boolean; erreur?: string | null } | undefined;
  largeur?: number | undefined;
  /** Journal du dossier (`dossiers.journal`) : lu à part, ses trois états ne bloquent pas le reste de l’écran. */
  journal?: EtatJournal | undefined;
  onReessayerJournal?: (() => void) | undefined;
  /** Action « Proposer un détective » : sans route au contrat (RFC-L53-001, point 7), l’écran ne la fournit pas et le bouton reste inactif. */
  onProposerDetective?: (() => void) | undefined;
  /** La personne qui lit l'écran est la dirigeante : le bouton ne lui propose pas « à Isabelle » (c'est elle). */
  lectriceDirigeante?: boolean | undefined;
  onAjouterLigne?: ((action: TypeAction) => void) | undefined;
  onCorrigerTemps?: (() => void) | undefined;
  onModifierEtapes?: (() => void) | undefined;
  /** Étape regardée dans la frise (identifiant) ; absente, l'étape en cours. L'état vit dans le conteneur (aucun hook ici). */
  etapeChoisie?: string | null | undefined;
  /** Choisir une étape de la frise (`null` : revenir à l'étape en cours). Sans rappel, la frise reste un simple repère. */
  onChoisirEtape?: ((id: string | null) => void) | undefined;
  /** Réglage BETA du courrier d'annonce : appris, null (défaut, sans réponse), ou absent (non chargé, rien n'est affiché). */
  courrierAnnonce?: ReglageAppris | null | undefined;
}

const LARGEUR_MOBILE = 700;
const LARGEUR_DEUX_COLONNES = 1000;

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const titreCarte: CSSProperties = { margin: 0, fontSize: 18 };
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13 };
const boutonAction: CSSProperties = { ...styleBoutonPrincipal, fontSize: 14, minHeight: CIBLE_MIN };
const lienBouton: CSSProperties = { ...styleBouton, fontSize: 14, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' };
const cibleLien: CSSProperties = { display: 'inline-flex', alignItems: 'center', minHeight: CIBLE_MIN, textDecoration: 'none' };
const pastille = (fond: string, texte: string): CSSProperties => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: fond,
  color: texte,
  borderRadius: 999,
  padding: '2px 10px',
});

const COULEUR_ETAT: Readonly<Record<EtatEtape, string>> = {
  fait: '#2F7D5B',
  en_cours: '#E5521F',
  suivante: '#F2B7A6',
  a_venir: '#E2E1EA',
  saute: '#C9CBE0',
};
const TON: Readonly<Record<TonDelai, [string, string]>> = {
  urgent: ['#FDECEA', COULEURS.rouge],
  proche: [COULEURS.ambreFond, COULEURS.ambre],
  normal: ['#EEF0F8', COULEURS.marine],
  termine: ['#E4F3EC', '#1F5E43'],
};
const MANDAT = MANDAT_LIBELLE;

const dateFr = (iso: string): string => iso.split('-').reverse().join('/');
const pluriel = (n: number, mot: string): string => `${n} ${mot}${n > 1 ? 's' : ''}`;

/** Type de mission porté par le dossier (`typeCode`, libellé s'il est fourni) ; absent : rien n'est affiché (lot 4, point 6). */
const typeMission = (dossier: DossierVue): string | null => {
  const d = dossier as DossierVue & { typeCode?: string | null; typeLabel?: string | null };
  return d.typeLabel ?? d.typeCode ?? null;
};

/** Réglage BETA du courrier d'annonce rendu visible : sa valeur, quand et où il s'applique (lot 4, point 7). */
function CourrierAnnonce({ reglage }: { reglage: ReglageAppris | null }) {
  const textes = reglagesVisibles.courrierAnnonce;
  const question = QUESTIONS_BETA.find((q) => q.cle === textes.cle);
  const code = reglage ? String(reglage.value) : (question?.defaut ?? null);
  const option = question?.options.find((o) => o.code === code);
  return (
    <section aria-label={textes.titre} style={carte}>
      <h2 style={titreCarte}>{textes.titre}</h2>
      <p style={{ margin: '0 0 4px', fontWeight: 600 }}>{option?.libelle ?? String(code ?? '')}</p>
      <p style={{ ...discret, margin: '0 0 4px' }}>
        {reglage ? textes.appris.replace('{auteur}', reglage.updatedByDisplayName) : textes.parDefaut}
      </p>
      <p style={{ margin: 0 }}>{textes.quand}</p>
      <p style={{ margin: 0 }}>{textes.ou}</p>
    </section>
  );
}

function Parcours({
  dossier,
  mobile,
  choisie,
  onChoisir,
}: {
  dossier: DossierVue;
  mobile: boolean;
  choisie: string | null;
  onChoisir: ((id: string | null) => void) | undefined;
}) {
  const etapes = resumeParcours(dossier.steps);
  const consultee = repartirParcours(dossier.steps, choisie).consultee?.id ?? null;
  const type = typeMission(dossier);
  // « Qui est venu vous voir ? » : rendu par l'API à l'équipe seulement (clé absente ailleurs) ; libellé de la donnée versionnée.
  const venu = dossier.venuPar ? libelleVenuPar(dossier.venuPar) : null;
  return (
    <section aria-label="Parcours du dossier" style={carte}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={titreCarte}>Parcours du dossier</h2>
        {type ? <span style={discret}>{`Type de mission : ${type}`}</span> : null}
        {venu ? <span style={discret}>{`${PREMIER_CONTACT.question} ${venu}`}</span> : null}
        <span style={discret}>
          {onChoisir ? 'Choisissez une étape pour la consulter' : 'Squelette commun, étapes ajustées pour ce dossier'}
        </span>
      </div>
      <ol
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          fontSize: 12,
          textAlign: 'center',
          gap: 6,
          ...(mobile
            ? { display: 'flex', overflowX: 'auto' }
            : { display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, etapes.length)}, minmax(0, 1fr))` }),
        }}
      >
        {etapes.map((e) => {
          const contenu = (
            <>
              <span aria-hidden="true" style={{ height: 8, borderRadius: 4, background: COULEUR_ETAT[e.etat], width: '100%' }} />
              <b>{e.code}</b>
              <span>{e.libelle}</span>
              <span style={{ color: COULEURS.ardoise }}>{LIBELLE_ETAT[e.etat]}</span>
              {e.motifSaut ? <span style={{ color: COULEURS.ardoise }}>{e.motifSaut}</span> : null}
            </>
          );
          return (
            <li
              key={e.id}
              data-etat={e.etat}
              {...(e.etat === 'en_cours' ? { 'aria-current': 'step' as const } : {})}
              style={{ display: 'flex', flexDirection: 'column', gap: 4, ...(mobile ? { flex: '0 0 110px' } : {}) }}
            >
              {onChoisir ? (
                <button
                  type="button"
                  data-etape-id={e.id}
                  aria-pressed={e.id === consultee}
                  onClick={() => onChoisir(e.id)}
                  style={{
                    ...styleBouton,
                    minHeight: CIBLE_MIN,
                    padding: '6px 4px',
                    fontSize: 12,
                    flexDirection: 'column',
                    gap: 4,
                    display: 'flex',
                    alignItems: 'center',
                    ...(e.id === consultee ? { border: `2px solid ${COULEURS.marine}`, background: COULEURS.surface } : {}),
                  }}
                >
                  {contenu}
                </button>
              ) : (
                contenu
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * Aucune route du contrat ne porte « proposer un détective » (RFC-L53-001, point 7) : l’écran ne fournit pas de
 * rappel, donc le bouton reste inactif (« Disponible prochainement »), jamais cliquable sans effet. Il ne s’active
 * que si le mandat est signé ET qu’un rappel est câblé à une route.
 */
function Blocage({
  dossier,
  aujourdhui,
  onProposerDetective,
  lectriceDirigeante,
}: {
  dossier: DossierVue;
  aujourdhui: string;
  onProposerDetective: (() => void) | undefined;
  lectriceDirigeante: boolean;
}) {
  if (!dossier.blocker) return null;
  const jours = joursDepuis(dossier.blocker.since, aujourdhui);
  const mandatSigne = dossier.mandateStatus === 'signe';
  const actif = mandatSigne && onProposerDetective !== undefined;
  const presente = presenterBlocage(dossier.blocker);
  const libelleDetective = lectriceDirigeante ? 'Proposer un détective' : 'Proposer un détective à Isabelle';
  return (
    <section aria-label="Blocage" style={{ ...carte, background: '#FDF4F0', border: '1px solid #F2B7A6', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: COULEURS.rouge, letterSpacing: '0.05em' }}>
        {`BLOCAGE · DEPUIS ${pluriel(jours, 'jour')}`.toUpperCase()}
      </span>
      <b style={{ fontSize: 17 }}>{presente.libelle}</b>
      {presente.mention ? <span style={{ fontSize: 14 }}>{presente.mention}</span> : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
        {!presente.actionDetective ? null : actif ? (
          <button type="button" style={boutonAction} onClick={onProposerDetective}>
            {libelleDetective}
          </button>
        ) : (
          <button
            type="button"
            disabled
            aria-disabled="true"
            aria-describedby="raison-detective"
            style={{ ...boutonAction, opacity: 0.5, cursor: 'not-allowed' }}
          >
            {libelleDetective}
          </button>
        )}
        <Lien vers={avecDossier(CHEMINS_VOISINS.arbre.chemin, dossier.id)} enfants="Voir l’arbre" style={lienBouton} />
      </div>
      {!presente.actionDetective || actif ? null : mandatSigne ? (
        <span id="raison-detective" style={{ fontSize: 13, color: COULEURS.ardoise }}>
          Disponible prochainement
        </span>
      ) : (
        <span id="raison-detective" style={{ fontSize: 13, color: COULEURS.rouge }}>
          Le mandat de recherche doit être signé avant toute recherche d’héritier.
        </span>
      )}
    </section>
  );
}

function Journal({ journal, onReessayer }: { journal: EtatJournal; onReessayer: (() => void) | undefined }) {
  return (
    <section id="journal" aria-label="Journal du dossier" style={carte}>
      <h2 style={titreCarte}>Journal du dossier</h2>
      {journal.etat === 'chargement' ? (
        <p role="status" style={{ margin: 0 }}>
          Chargement du journal…
        </p>
      ) : null}
      {journal.etat === 'erreur' ? (
        <>
          <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
            {journal.message}
          </p>
          <button type="button" style={{ ...boutonAction, alignSelf: 'flex-start' }} onClick={onReessayer}>
            Réessayer
          </button>
        </>
      ) : null}
      {journal.etat === 'pret' && journal.entrees.length === 0 ? (
        <p style={{ margin: 0, color: COULEURS.ardoise }}>Le journal du dossier est vide.</p>
      ) : null}
      {journal.etat === 'pret' && journal.entrees.length > 0 ? (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
          {journal.entrees.map((e) => (
            <li key={e.id}>
              <span style={{ color: COULEURS.ardoise }}>{`${dateFr(e.at.slice(0, 10))} · `}</span>
              {e.summary}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

const TON_MANDAT: Readonly<Record<DossierVue['mandateStatus'], 'bonne-voie' | 'attention' | 'bloque' | 'neutre'>> = {
  signe: 'bonne-voie',
  envoye: 'attention',
  brouillon: 'attention',
  absent: 'neutre',
  refuse: 'bloque',
  revoque: 'bloque',
};

/** Colonne de droite de la vue d'ensemble (reference/03) : délais légaux, mandat de recherche, tableau des vivants. */
function ColonneDroite({ dossier, aujourdhui, avecDelais }: { dossier: DossierVue; aujourdhui: string; avecDelais: boolean }) {
  return (
    <aside
      data-colonne-droite=""
      aria-label="Repères du dossier"
      style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}
    >
      {avecDelais ? (
        <section aria-label="Délais légaux" data-detail="delais" style={carte}>
          <h2 style={titreCarte}>Délais légaux</h2>
          <div style={{ fontSize: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dossier.deadlines.length === 0 ? <span>Aucun délai légal enregistré.</span> : null}
            {dossier.deadlines.map((d) => {
              const delai = presenterDelai(d, aujourdhui);
              const [fond, texte] = TON[delai.ton];
              return (
                <div key={d.id} data-ton={delai.ton}>
                  <b>{d.label}</b>
                  <br />
                  <span style={{ color: COULEURS.ardoise }}>{`Échéance ${dateFr(d.dueAt)}`}</span>
                  <br />
                  <span style={pastille(fond, texte)}>{delai.texte}</span>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
      <section aria-label="Mandat de recherche" style={carte}>
        <h2 style={titreCarte}>Mandat de recherche</h2>
        <div style={{ fontSize: 14 }}>
          <Pastille ton={TON_MANDAT[dossier.mandateStatus]}>
            <b>{`Mandat de recherche : ${MANDAT[dossier.mandateStatus]}`}</b>
          </Pastille>
        </div>
      </section>
      <section aria-label="Tableau des vivants" style={carte}>
        <h2 style={titreCarte}>Tableau des vivants</h2>
        <div
          role="progressbar"
          aria-label="Complétude du tableau des vivants"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={dossier.completeness}
          style={{ height: 10, borderRadius: 999, background: '#E2E1EA', overflow: 'hidden' }}
        >
          <div style={{ width: `${dossier.completeness}%`, height: '100%', background: '#2F7D5B' }} />
        </div>
        <span style={{ fontSize: 14 }}>{`Complet à ${dossier.completeness} %`}</span>
      </section>
    </aside>
  );
}

function Forfait({
  forfait,
  onAjouterLigne,
}: {
  forfait: NonNullable<VueEnsembleProps['forfait']>;
  onAjouterLigne: ((a: TypeAction) => void) | undefined;
}) {
  const lignes = compteurForfait(forfait.inclus, forfait.consomme);
  return (
    <section aria-label="Forfait inclus" style={carte}>
      <h2 style={titreCarte}>Forfait inclus</h2>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
        {lignes.map((l) => (
          <li
            key={l.action}
            data-depasse={l.depasse ? 'true' : undefined}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>
              {l.inclus === null
                ? `${l.libelle} : ${l.consomme} (forfait à définir)`
                : `${l.libelle} : ${l.consomme} sur ${l.inclus}${l.depasse ? ' · forfait dépassé' : ''}`}
            </span>
            {l.depasse ? (
              <button type="button" style={boutonAction} onClick={() => onAjouterLigne?.(l.action)}>
                Ajouter la ligne supplémentaire
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Chrono({
  chrono,
  onCorrigerTemps,
}: {
  chrono: NonNullable<VueEnsembleProps['chrono']>;
  onCorrigerTemps: (() => void) | undefined;
}) {
  return (
    <section
      aria-label="Temps passé"
      style={{
        ...carte,
        padding: '10px 14px',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span style={{ fontSize: 13, color: COULEURS.ardoise }}>
        {`Temps passé : ${formaterDuree(chrono.totalMs)} · ${chrono.enCours ? 'chronomètre en marche' : 'chronomètre arrêté'}`}
      </span>
      <button type="button" style={{ ...styleBouton, fontSize: 13 }} onClick={onCorrigerTemps}>
        Corriger
      </button>
      {chrono.erreur ? (
        <p role="alert" style={{ flex: '1 1 100%', margin: 0, fontSize: 13, color: COULEURS.rouge }}>
          {chrono.erreur}
        </p>
      ) : null}
    </section>
  );
}

function Recherche({ recherche }: { recherche: readonly EtapeRecherche[] }) {
  return (
    <section aria-label="Recherche de titre" style={carte}>
      <h2 style={titreCarte}>Recherche de titre</h2>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
        {recherche.map((r) => {
          const garde = peutActiver(recherche, r.code);
          const verrouillee = !garde.ok && r.etat !== 'fait' && r.etat !== 'sans_objet';
          return (
            <li
              key={r.code}
              data-code={r.code}
              {...(verrouillee ? { 'data-verrouillee': 'true' } : {})}
              data-statut={r.etat}
              style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: verrouillee ? 0.7 : 1 }}
            >
              <span>
                <b>{r.code}</b> {`${r.libelle} · ${LIBELLE_RECHERCHE[r.etat]}`}
              </span>
              {r.motif ? <span style={discret}>{r.motif}</span> : null}
              {verrouillee && garde.raison ? <span style={discret}>{garde.raison}</span> : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

const LIBELLE_RECHERCHE: Readonly<Record<EtapeRecherche['etat'], string>> = {
  a_faire: 'à faire',
  en_cours: 'en cours',
  fait: 'faite',
  sans_objet: 'sans objet',
};

/** D03 : vue d’ensemble d’un dossier pour l’équipe MAAT. Aucun hook : l’état vit dans le conteneur. */
export function VueEnsemble(props: VueEnsembleProps): ReactNode {
  const { dossier, aujourdhui } = props;
  const mobile = (props.largeur ?? 1280) < LARGEUR_MOBILE;
  const prochaine = dossier.nextAction;
  const rep = repartirParcours(dossier.steps, props.etapeChoisie);
  const manques = ceQuiManque(dossier, aujourdhui);
  const phaseVue = rep.consultee?.code ?? null;
  // Blocs propres à une étape : leur étape vient du référentiel versionné, jamais d'ici.
  const blocs: { cle: string; titre: string; phase: string; place: PlaceBloc; rendu: ReactNode }[] = [
    ...(props.forfait
      ? [
          {
            cle: 'forfait',
            titre: 'Forfait inclus',
            phase: REFERENTIEL_L53.blocsParPhase.forfait,
            rendu: <Forfait forfait={props.forfait} onAjouterLigne={props.onAjouterLigne} />,
          },
        ]
      : []),
    ...(props.recherche
      ? [
          {
            cle: 'recherche',
            titre: 'Recherche de titre',
            phase: REFERENTIEL_L53.blocsParPhase.recherche,
            rendu: <Recherche recherche={props.recherche} />,
          },
        ]
      : []),
  ].map((b) => ({ ...b, place: blocPlace(b.phase, phaseVue) }));
  return (
    <section
      data-ecran="D03"
      data-ecran-dossier="D03"
      style={{
        background: COULEURS.surface,
        color: COULEURS.marineFonce,
        fontFamily: POLICE_EQUIPE,
        fontSize: 15,
        lineHeight: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <div style={{ fontSize: 13, color: COULEURS.ardoise }}>
        <Lien vers={CHEMINS_VOISINS.pilotage.chemin} enfants="Dossiers" style={{ ...cibleLien, color: COULEURS.ardoise }} />{' '}
        {`/ ${dossier.reference}`}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1
            style={{
              margin: 0,
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: mobile ? 26 : 34,
              lineHeight: 1.2,
              color: COULEURS.marine,
            }}
          >
            {dossier.title}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {dossier.status === 'bloque' || dossier.blocker ? (
              <Pastille ton="bloque" data-etat-dossier="bloque">
                Bloqué
              </Pastille>
            ) : dossier.status === 'actif' ? (
              <Pastille ton="bonne-voie" data-etat-dossier="bonne-voie">
                En bonne voie
              </Pastille>
            ) : null}
            {props.metier?.type ? (
              <Pastille ton="equipe" data-champ="type-dossier">
                {props.metier.type.libelle}
              </Pastille>
            ) : null}
            {props.metier?.statut ? (
              <Pastille ton="attention" data-champ="statut-metier">
                {props.metier.statut.libelle}
              </Pastille>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', fontSize: 14, color: COULEURS.ardoise }}>
            {props.mandantNom ? <span>{`Mandante : ${props.mandantNom}`}</span> : null}
            <span>{pluriel(dossier.heirsCount, 'héritier')}</span>
            {dossier.etudeName ? <span>{`Étude : ${dossier.etudeName}`}</span> : null}
          </div>
        </div>
        <button type="button" style={styleBouton} onClick={props.onModifierEtapes}>
          Modifier les étapes
        </button>
      </div>

      <NavSections dossierId={dossier.id} actif="ensemble" defilant={mobile} />

      <div
        data-colonnes=""
        style={{
          display: 'grid',
          gap: 20,
          alignItems: 'start',
          gridTemplateColumns: mobile || (props.largeur ?? 1280) < LARGEUR_DEUX_COLONNES ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 340px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          {props.chrono ? <Chrono chrono={props.chrono} onCorrigerTemps={props.onCorrigerTemps} /> : null}

          {props.geometre ? (
            <div role="alert" style={{ ...carte, background: COULEURS.ambreFond, border: `1px solid ${COULEURS.ambre}`, gap: 4 }}>
              <b>Géomètre nécessaire</b>
              <span style={{ fontSize: 14 }}>
                Le titre est ancien ou sans référence cadastrale : une pré-étude de concordance (REC-07) est proposée.
              </span>
            </div>
          ) : null}

          <Parcours dossier={dossier} mobile={mobile} choisie={props.etapeChoisie ?? null} onChoisir={props.onChoisirEtape} />
          {props.courrierAnnonce !== undefined ? <CourrierAnnonce reglage={props.courrierAnnonce} /> : null}

          <section
            aria-label={rep.enCours ? 'Où j’en suis' : 'Étape consultée'}
            data-etape-courante={rep.consultee?.id}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <div style={{ ...carte, gap: 6 }}>
              <h2 style={{ ...titreCarte, fontSize: 20 }}>
                {rep.consultee
                  ? `${rep.enCours ? 'Où j’en suis' : 'Étape consultée'} : ${rep.consultee.code} · ${rep.consultee.libelle}`
                  : 'Où j’en suis'}
              </h2>
              {rep.consultee ? (
                <span style={discret}>
                  {[
                    LIBELLE_ETAT[rep.consultee.etat],
                    rep.consultee.echeance ? `échéance ${dateFr(rep.consultee.echeance.slice(0, 10))}` : null,
                    rep.consultee.motifSaut,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              ) : (
                <span style={discret}>
                  Aucune étape n’est encore définie pour ce dossier. Utilisez « Modifier les étapes » pour en ajouter.
                </span>
              )}
              {!rep.enCours && props.onChoisirEtape ? (
                <button type="button" style={{ ...styleBouton, alignSelf: 'flex-start' }} onClick={() => props.onChoisirEtape?.(null)}>
                  Revenir à l’étape en cours
                </button>
              ) : null}
            </div>

            {rep.enCours ? (
              <>
                <section aria-label="Ce qui manque pour avancer" style={carte}>
                  <h3 style={{ ...titreCarte, fontSize: 17 }}>Ce qui manque pour avancer</h3>
                  {manques.length === 0 ? (
                    <p style={{ margin: 0 }}>Rien ne manque pour avancer sur cette étape.</p>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 15 }}>
                      {/* Le blocage et l'action suivante ont leur propre encadré juste dessous : on ne les répète pas ici. */}
                      {manques
                        .filter((m) => m.code !== 'blocage' && m.code !== 'action')
                        .map((m, i) => (
                          <li key={`${m.code}-${i}`} data-manque={m.code}>
                            {m.texte}
                          </li>
                        ))}
                      {manques.every((m) => m.code === 'blocage' || m.code === 'action') ? (
                        <li>
                          {manques.some((m) => m.code === 'blocage')
                            ? 'Un blocage est à lever (voir ci-dessous).'
                            : 'Une action est à faire (voir ci-dessous).'}
                        </li>
                      ) : null}
                    </ul>
                  )}
                </section>
                <Blocage
                  dossier={dossier}
                  aujourdhui={aujourdhui}
                  onProposerDetective={props.onProposerDetective}
                  lectriceDirigeante={props.lectriceDirigeante === true}
                />
                <section aria-label="Prochaines actions" style={carte}>
                  <h2 style={titreCarte}>Prochaines actions</h2>
                  <div style={{ fontSize: 14, padding: '10px 12px', background: COULEURS.surface, borderRadius: 8 }}>
                    {prochaine ?? 'Aucune action prévue pour le moment.'}
                  </div>
                </section>
              </>
            ) : null}

            {blocs
              .filter((b) => b.place === 'ici')
              .map((b) => (
                <div key={b.cle} data-bloc={b.cle}>
                  {b.rendu}
                </div>
              ))}
          </section>

          {rep.passees.length > 0 || blocs.some((b) => b.place === 'passe' && !rep.passees.some((e) => e.code === b.phase)) ? (
            <section data-groupe="etapes-passees" aria-label="Étapes passées" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h2 style={{ ...titreCarte, fontSize: 16, color: COULEURS.ardoise }}>Étapes passées, à consulter</h2>
              {rep.passees.map((e, i) => (
                <details key={e.id} data-etape-passee={e.id} style={{ ...carte, padding: '4px 14px' }}>
                  <summary style={{ ...cibleLien, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}>
                    {`${e.code} · ${e.libelle}`}
                    <span style={{ ...discret, fontWeight: 400, marginLeft: 8 }}>{LIBELLE_ETAT[e.etat]}</span>
                  </summary>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0 12px', fontSize: 14 }}>
                    {e.motifSaut ? <span>{`Étape sautée : ${e.motifSaut}`}</span> : null}
                    {e.echeance ? <span style={discret}>{`Échéance : ${dateFr(e.echeance.slice(0, 10))}`}</span> : null}
                    {blocs
                      .filter((b) => b.place === 'passe' && b.phase === e.code && rep.passees.findIndex((x) => x.code === e.code) === i)
                      .map((b) => (
                        <div key={b.cle} data-bloc={b.cle}>
                          {b.rendu}
                        </div>
                      ))}
                    {props.onChoisirEtape ? (
                      <button
                        type="button"
                        style={{ ...styleBouton, alignSelf: 'flex-start' }}
                        onClick={() => props.onChoisirEtape?.(e.id)}
                      >
                        Ouvrir cette étape
                      </button>
                    ) : null}
                  </div>
                </details>
              ))}
              {blocs
                .filter((b) => b.place === 'passe' && !rep.passees.some((e) => e.code === b.phase))
                .map((b) => (
                  <details key={b.cle} data-bloc-passe={b.cle} style={{ ...carte, padding: '4px 14px' }}>
                    <summary
                      style={{ ...cibleLien, cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
                    >{`${b.titre} (étape ${b.phase})`}</summary>
                    <div style={{ padding: '4px 0 12px' }}>{b.rendu}</div>
                  </details>
                ))}
            </section>
          ) : null}

          {rep.futures.length > 0 || blocs.some((b) => b.place === 'avenir' && !rep.futures.some((e) => e.code === b.phase)) ? (
            <section data-groupe="a-venir" aria-label="Étapes à venir" style={{ fontSize: 14, color: COULEURS.ardoise }}>
              <h2 style={{ ...titreCarte, fontSize: 16, color: COULEURS.ardoise }}>À venir</h2>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rep.futures.map((e) => {
                  const annonces = blocs.filter((b) => b.place === 'avenir' && b.phase === e.code);
                  return (
                    <li key={e.id} data-etape-future={e.id}>
                      {`${e.code} · ${e.libelle}`}
                      {annonces.length > 0 ? ` (${annonces.map((b) => b.titre).join(', ')})` : ''}
                    </li>
                  );
                })}
                {blocs
                  .filter((b) => b.place === 'avenir' && !rep.futures.some((e) => e.code === b.phase))
                  .map((b) => (
                    <li key={b.cle} data-bloc-avenir={b.cle}>{`${b.titre} (étape ${b.phase})`}</li>
                  ))}
              </ul>
            </section>
          ) : null}

          {props.journal ? <Journal journal={props.journal} onReessayer={props.onReessayerJournal} /> : null}
        </div>
        <ColonneDroite dossier={dossier} aujourdhui={aujourdhui} avecDelais />
      </div>
    </section>
  );
}
